// Shadow draft comparison (docs/agent-draft-shadow.md). Admin-only, never in
// View as Agent: the agent's draft for the current suggestion, with its steps
// and the tools it called, shown next to the real suggestion (2026-10-16, side
// by side; docs/decisions/2026-10-16-shadow-draft-side-by-side.md).
//
// The A2 layout (2026-10-23): sections on a light grey panel, each its own
// white block with a small icon and an uppercase label (Summary, What's next,
// Steps, Reply). The agent's actions are step previews built by the server
// with the pipeline's own builders (ai/shadow-step-previews.js), editable
// inline; an edit is saved on the shadow draft only. Nothing here processes
// or sends anything: there is no Process or Send, and the bottom line says
// so. An admin copies the reply into the normal panel, where every normal
// send gate applies.
//
// The layout's pieces are AgentDraftLayout.jsx's (2026-10-23), shared with
// the main panel's agent draft so the two never drift: the condensed What's
// next, one placeholder line under the reply, the Details collapsed.
//
// The data comes from useShadowDraft(); the layout (side by side, the
// collapsed rail, or stacked on a narrow viewport) is SuggestionPanel.jsx's,
// and there is one collapse control: the toggle here.
import { useEffect, useRef, useState } from 'react'
import { EbayRefundRouteNote } from './EbayRefundRouteNote.jsx'
import { Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputAdornment, Link, MenuItem, Radio, RadioGroup, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { A2, WRAP, INPUT_SX, TEXTAREA_SX, FieldLabel, AgentDraftShell, AgentDraftHeader, RunStatusChip, SummaryBlock, NextStepsSection, StepsSection, StepRow, ReplySection as A2ReplySection, DetailsFooter, PlaceholderLine, PlaceholderHighlight, GeneratedAtLine, PrimaryActionArea } from './AgentDraftLayout.jsx'
import { ViewCalls } from './ViewCalls.jsx'
import { toggleStepCheck, stepDependencyNote, processAllButtonLabel } from './processAll.js'
import { replyDestinationLine, customerReturnTrackingLine, STEP_MARK_NOT_NEEDED_LABEL, STEP_NOT_NEEDED_NOTE } from '@cxhub/shared'
// The Return Status stage choice (2026-10-29): the main panel's own controls.
import TaskPathChoiceControls from './TaskPathChoiceControls.jsx'
import { runDetailsLine, placeholderLine } from './agentDraftLayout.js'
import { CopyButton } from './CopyButton.jsx'
import { fetchInvoiceAttachments as realFetchInvoiceAttachments, saveShadowStepEdit as realSaveShadowStepEdit, runDispositionCheck as realRunDispositionCheck, saveShadowResolution as realSaveShadowResolution } from './api.js'
// Replacement or refund on the step (2026-10-27).
import ResolutionBar from './ResolutionBar.jsx'
import { shadowResolutionState, shadowDecisionOf } from './resolutionSwitch.js'
// The return step's label section and disposition check (2026-10-27): the
// main panel's own component and rules, so the two panels can't drift.
import ReturnLabelFields from './ReturnLabelFields.jsx'
import { DISPOSITION_LABEL, DISPOSITION_HELPER, CHECK_STALE_TEXT, DISPOSITION_OPTIONS, overriddenText, checkIsStale, returnLabelFieldsShown } from './returnStepCheck.js'
import { shadowStageChoice, shadowStageReply, shadowStageStepShown, STAGE_REPLY_PLACEHOLDER } from './shadowStageChoice.js'
import { toolCallLines, shadowTranslationFor, shadowReplyText, shadowInvoiceIds, invoiceDataUrlFor, invoiceOpenErrorText, invoiceNotAttachableLine, dataUrlToBlob, shadowSourcesLine, shadowLimitWarning, comparisonTitle, shadowStepRows, stepEditValues, stepIsEditable, replyHeaderRows, confidenceView, refundTypeChange, editedAtLabel, PREVIEW_ONLY_TEXT, STEP_SAVE_DELAY_MS, shadowProcessableCount, SHADOW_BUTTON_PREVIEW_LINE, shadowSalesHoldPath, PIPELINE_STEPS_NOT_USED_TITLE, PIPELINE_NOTE_NOT_USED_TITLE, pipelineNoteNotUsed } from './shadowDraft.js'
// The main panel's own pieces (2026-10-26, every step builds): the packages
// editor of the return label step and the refund comments of the Issue Refund
// step, shared, not copied.
import PackagesEditor from './PackagesEditor.jsx'
import { FULL_REFUND_COMMENT, PARTIAL_REFUND_COMMENT } from './RefundModal.jsx'
import { groupItemsForTable, isComponentBlockedByKit, toggleKitAwareSelection } from './suggestionActionInputs.js'
import { shadowStepCall, returnRefundBreakdown, payerForReason, DEDUCTION_SOURCE_LABELS, zeroDollarLabelOn } from '@cxhub/shared'
import { ZeroDollarRmaBanner, ReturnLabelBanners, ReplacementLabelOptional } from './ReturnLabelPending.jsx'
import { useZeroDollarTiming, useReturnLabels } from './useReturnLabels.js'
import { orderWaitingBanners, shadowReplacementLabelHeld, isShadowReplacementRow } from './pendingLabels.js'
// "Found the right part?" (2026-10-30, correctPart.js).
import CorrectPartBox from './CorrectPartBox.jsx'
import { correctPartBoxApplies, stepsAfterCorrectPart, confidenceAfterCorrectPart } from './correctPart.js'

// The primitives are AgentDraftLayout.jsx's, shared with the main panel's
// agent draft (2026-10-23), so the two never drift.
function StatusChips({ draft }) {
  return (
    <>
      <Chip size="small" label="admin only" variant="outlined" data-testid="agent-draft-admin-only" sx={{ height: 20, fontSize: '0.7rem' }} />
      {/* The pipeline's draft not used (2026-10-21) has no run status. */}
      {!draft.pipelineNotUsed && <RunStatusChip status={draft.status === 'ok' ? 'ok' : 'error'} />}
    </>
  )
}

// The English of a reply, summary, note or step, under it, when Translate is
// on: the same look as a message's English box (App.jsx).
function EnglishBox({ text, testId }) {
  if (!text) return null
  return (
    <Box data-testid={testId} sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: A2.rule }}>
      <Typography variant="caption" sx={{ color: A2.label }} fontWeight={600}>English</Typography>
      <Typography sx={{ fontSize: 13, color: A2.label, whiteSpace: 'pre-wrap', ...WRAP }}>{text}</Typography>
    </Box>
  )
}

// The invoices get_invoices found (2026-10-19, prod contact 7972656): an
// "Open INV…" link each, whether they are attached, and what the agent saw.
// A click opens the real invoice in a new tab as a blob: URL, from GET
// /:id/invoice-attachments, fetched once per panel and reused. The tab is
// opened during the click so a popup blocker allows it, then pointed at the
// invoice; on a failure it closes and the box says why (2026-10-29: the
// preload's reason, as the reply box says it).
// The agent attached invoices it checked (2026-10-29): the preload is read
// when the box shows, and when it cannot render them the box says "Checked
// by the agent, but it can't be attached: <reason>" in place of "Attached to
// the reply".
function InvoicesBox({ draft, fetchInvoices }) {
  const ids = shadowInvoiceIds(draft)
  const cache = useRef(null)
  const [error, setError] = useState(null)
  const [preload, setPreload] = useState(null)
  const shown = Boolean(ids.length || draft.attachInvoices || draft.invoiceCheck)
  const load = () => {
    if (!cache.current) cache.current = fetchInvoices(draft.conversationId).catch((err) => { cache.current = null; throw err })
    return cache.current
  }
  const checkAttachable = shown && draft.attachInvoices && ids.length > 0
  useEffect(() => {
    if (!checkAttachable) return undefined
    let cancelled = false
    load().then((r) => { if (!cancelled) setPreload(r) }).catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAttachable, draft.conversationId])
  if (!shown) return null
  const notAttachable = invoiceNotAttachableLine(draft, preload)
  const open = async (id) => {
    setError(null)
    const tab = window.open('', '_blank')
    try {
      const result = await load()
      const blob = dataUrlToBlob(invoiceDataUrlFor(result, id))
      if (!blob) throw new Error(invoiceOpenErrorText(result, id))
      const url = URL.createObjectURL(blob)
      if (tab) tab.location.href = url
      else window.open(url, '_blank')
    } catch (err) {
      if (tab) tab.close()
      setError(`Could not open ${id}: ${err.message}`)
    }
  }
  return (
    <Box data-testid="shadow-draft-invoices" sx={{ p: 1, border: 1, borderColor: A2.rule, borderRadius: '6px' }}>
      <FieldLabel>Invoices</FieldLabel>
      {ids.map((id) => (
        <Box key={id}>
          <Link component="button" type="button" underline="hover" data-testid="shadow-draft-invoice-link" onClick={() => open(id)} sx={{ fontSize: 13, color: A2.link }}>Open {id}</Link>
        </Box>
      ))}
      {notAttachable
        ? <Typography fontSize={13} color="error" data-testid="shadow-draft-invoice-not-attachable" sx={{ ...WRAP }}>{notAttachable}</Typography>
        : <Typography fontSize={13} data-testid="shadow-draft-invoice-attached">{draft.attachInvoices ? 'Attached to the reply' : 'Not attached'}</Typography>}
      {draft.invoiceCheck && <Typography fontSize={13} sx={{ whiteSpace: 'pre-wrap', ...WRAP }}>{draft.invoiceCheck}</Typography>}
      {error && <Typography fontSize={13} color="error" data-testid="shadow-draft-invoice-error">{error}</Typography>}
    </Box>
  )
}

// The item table: the order's lines, kits above their components, a
// checkbox and a quantity each, as on the main panel's step.
function ItemsField({ allItems, selection, onChange }) {
  return (
    <Box>
      <FieldLabel>Items</FieldLabel>
      <TableContainer sx={{ border: 1, borderColor: A2.border, borderRadius: '6px' }}>
        <Table size="small" data-testid="shadow-step-items" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.25, fontSize: 13, color: A2.text, borderColor: A2.rule } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupItemsForTable(allItems).map((it, ri) => {
              const entry = (selection ?? []).find((x) => x.sku === it.sku)
              const checked = Boolean(entry)
              const description = it.description ?? '—'
              return (
                <TableRow key={it.sku ?? `${it.description}-${ri}`} sx={it.itemType !== 'component' ? { bgcolor: 'action.hover' } : {}}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={checked}
                      disabled={!it.sku || isComponentBlockedByKit(it, allItems, selection)}
                      inputProps={{ 'aria-label': `Select ${it.sku ?? description}` }}
                      onChange={(ev) => onChange(toggleKitAwareSelection({ items: selection ?? [] }, it, allItems, ev.target.checked))} />
                  </TableCell>
                  <TableCell sx={{ '&.MuiTableCell-root': { pl: it.itemType === 'component' ? 3 : 1 } }}>{it.itemType === 'component' ? '↳ ' : ''}{it.sku ?? '—'}</TableCell>
                  <TableCell><Tooltip title={description}><span>{description.length > 30 ? `${description.slice(0, 30)}…` : description}</span></Tooltip></TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={entry?.quantity ?? it.quantity ?? 1} disabled={!checked}
                      sx={{ '& .MuiOutlinedInput-input': { py: 0.25, px: 0.5 } }}
                      inputProps={{ min: 1, max: it.quantity ?? 1, 'aria-label': `Quantity of ${it.sku}`, style: { width: 30, textAlign: 'right', fontSize: 13 } }}
                      onChange={(ev) => {
                        const qty = Math.max(1, Math.min(it.quantity ?? 1, Number(ev.target.value) || 1))
                        onChange((selection ?? []).map((x) => (x.sku === it.sku ? { ...x, quantity: qty } : x)))
                      }} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`)
const reasonOptionsOf = (preview) => preview?.options?.reasonCodes ?? []

const ADDRESS_FIELDS = [
  ['shippingName', 'Name'], ['shippingAddress1', 'Address 1'], ['shippingAddress2', 'Address 2'],
  ['shippingCity', 'City'], ['shippingRegion', 'State'], ['shippingPostalCode', 'ZIP'],
]

// One step preview: its number, title and system chip, the agent's sentence
// when it was not built, its fields inline, and View call. An edit is saved
// STEP_SAVE_DELAY_MS after the last change (and when the row goes away).
// checked / onCheck / checkNote (2026-10-26): the step's checkbox, saved with
// the step's edit (stepEdits[key].checked); nothing is processed here.
// decision / onDecision (2026-10-28, an eBay cancel request's steps): the
// request's decision in effect (the toggle on either eBay step shows it and
// changes it for the pair). A reject that sends eBay the order's tracking
// shows it as its own line (preview.thenLine).
// onValues (2026-10-29): the stage choice's values as they change (the stage,
// the date, notNeeded), so the reply follows the choice.
function ShadowStepRow({ index, preview, draft, saveEdit, checkDisposition = realRunDispositionCheck, checked = true, onCheck = null, checkNote = null, decision = null, onDecision = null, onValues = null }) {
  const [values, setValues] = useState(() => ({ ...(preview.values ?? {}), ...(preview.kind === 'task_path_choice' ? { notNeeded: preview.notNeeded === true } : {}) }))
  const [status, setStatus] = useState(preview.editedAt ? 'saved' : 'idle')
  const [editedAt, setEditedAt] = useState(preview.editedAt ?? null)
  const [error, setError] = useState(null)
  const [serverCalls, setServerCalls] = useState(() => (Array.isArray(preview.calls) ? { calls: preview.calls, callNote: preview.callNote ?? null } : null))
  const latest = useRef(values)
  const checkedRef = useRef(checked)
  const timer = useRef(null)
  const editable = stepIsEditable(preview, draft)
  // The checkbox is saved for any step the server built (2026-10-26), even
  // one with no fields.
  const savable = Boolean(!preview.readOnly && draft?.id && draft?.conversationId)
  const fields = preview.fields ?? []
  const editOf = () => ({
    ...stepEditValues(preview, latest.current),
    ...(checkedRef.current === false || preview.checked === false ? { checked: checkedRef.current } : {}),
    ...(preview.kind === 'task_path_choice' && (latest.current.notNeeded || preview.notNeeded) ? { notNeeded: latest.current.notNeeded === true } : {}),
  })
  const save = async () => {
    timer.current = null
    setStatus('saving')
    try {
      const res = await saveEdit(draft.conversationId, draft.id, preview.key, editOf())
      // A return step's calls are built on the server for the saved values
      // (return-refund.js's buildPlannedSteps()): take the new ones.
      const fresh = res?.shadowDraft?.stepPreviews?.find?.((x) => x.key === preview.key)
      if (fresh && Array.isArray(fresh.calls)) setServerCalls({ calls: fresh.calls, callNote: fresh.callNote ?? null })
      if (fresh?.editedAt) setEditedAt(fresh.editedAt)
      setStatus('saved')
      setError(null)
    } catch (err) {
      setStatus('error')
      setError(err?.message ?? 'unknown error')
    }
  }
  // A change still waiting is saved when the row goes away.
  useEffect(() => () => {
    if (timer.current) {
      clearTimeout(timer.current)
      saveEdit(draft.conversationId, draft.id, preview.key, editOf()).catch(() => {})
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // A change of the checkbox (this step's, or one that runs with it) is
  // saved the same way as a field edit.
  const firstCheck = useRef(true)
  useEffect(() => {
    if (firstCheck.current) { firstCheck.current = false; return }
    checkedRef.current = checked
    if (!savable) return
    if (timer.current) clearTimeout(timer.current)
    setStatus('pending')
    timer.current = setTimeout(save, STEP_SAVE_DELAY_MS)
  }, [checked]) // eslint-disable-line react-hooks/exhaustive-deps
  const update = (patch) => {
    const next = { ...latest.current, ...patch }
    latest.current = next
    setValues(next)
    onValues?.(next)
    if (!editable) return
    if (timer.current) clearTimeout(timer.current)
    setStatus('pending')
    timer.current = setTimeout(save, STEP_SAVE_DELAY_MS)
  }
  const reasonOptions = preview.options?.reasonCodes ?? []
  const zeroDollar = reasonOptions.find((o) => o.value === values.reasonCode)?.requiresZeroDollarRma
  const zeroDollarTypical = useZeroDollarTiming(Boolean(zeroDollar))
  const viewCall = preview.call ? shadowStepCall(preview, values) : null
  // Several calls (a return's RMA create and label create, a label's
  // recovery): one "View calls (N)" link, each call labeled in its modal.
  const callList = serverCalls?.calls ?? []
  const callNote = serverCalls ? serverCalls.callNote : preview.callNote
  // A return's payer and refund breakdown, live as the values change
  // (@cxhub/shared's returnRefundBreakdown(), the server's own math).
  const isReturn = preview.kind === 'return' && preview.built
  const payer = isReturn ? payerForReason(reasonOptionsOf(preview), values.reasonCode) : null
  // The rate quote of the check on the step (a Check again's) when it has one.
  const refundFacts = values.check?.rateQuote && preview.options?.refund ? { ...preview.options.refund, rateQuote: values.check.rateQuote } : (preview.options?.refund ?? null)
  const breakdown = isReturn ? returnRefundBreakdown({ refund: refundFacts, values, payer }) : null
  // The disposition check (2026-10-27): the check's result is selected; an
  // edit of the items or reason code makes it stale; Check again runs it
  // (POST /disposition-check, reads only) and saves its answer on the step.
  const [checking, setChecking] = useState(false)
  const [checkError, setCheckError] = useState(null)
  const stale = isReturn && checkIsStale(values.check, values)
  const canCheck = isReturn && Boolean(values.reasonCode) && (values.items ?? []).length > 0 && Boolean(draft?.id && draft?.conversationId)
  const runCheck = async () => {
    setChecking(true)
    setCheckError(null)
    try {
      const res = await checkDisposition(draft.conversationId, {
        erpOrderId: preview.order, items: values.items ?? [], reasonCode: String(values.reasonCode),
        shadowDraftId: draft.id, stepKey: preview.key,
        ...(payer === 'customer' && values.shippingPaymentOption ? { shippingPaymentOption: values.shippingPaymentOption } : {}),
      })
      if (!res?.success || !res.check) throw new Error(res?.error ?? 'no disposition came back')
      // Already shipped back (2026-10-28): the step stays an RMA whatever the
      // check says; the check shows as overridden.
      update({ check: res.check, disposition: preview.alreadyShippedBack ? 'return' : res.check.disposition })
    } catch (err) {
      setCheckError(err?.message ?? 'unknown error')
    } finally {
      setChecking(false)
    }
  }
  const labelShown = isReturn && fields.includes('packages') && returnLabelFieldsShown({
    disposition: values.disposition, hasExistingReturn: preview.options?.label?.hasExistingReturn, ebay: preview.options?.label?.ebay,
    shippingPaymentOption: payer === 'customer' ? (values.shippingPaymentOption || 'customerCost') : null,
    alreadyShippedBack: Boolean(preview.alreadyShippedBack),
  })
  const showDeductionInput = isReturn && fields.includes('labelDeduction') && payer === 'customer' && values.disposition !== 'scrap' && values.shippingPaymentOption !== 'customerShipsOwn'
  // Already shipped back (2026-10-28): why the step overrode the check.
  const asbReason = preview.alreadyShippedBack && values.disposition === 'return' && values.check?.disposition === 'scrap' ? preview.alreadyShippedBack.reason : null
  const id = (f) => `shadow-step-${preview.key}-${f}`
  // View calls (2026-10-27): one small link at the end of the title row.
  const callLinks = viewCall ? [{ label: null, call: viewCall }] : callList.map((c) => ({ label: c.label, call: c.call }))
  return (
    <StepRow number={index + 1} title={preview.title} system={preview.system} testId="shadow-step" titleTestId="shadow-step-title" systemTestId="shadow-step-system" dataKey={preview.key}
      checked={checked} onCheck={onCheck} checkNote={checkNote} checkTestId="shadow-step-check"
      right={callLinks.length ? <ViewCalls calls={callLinks} title={preview.title} testId="shadow-step-view-call" /> : null}>
        {preview.text && !preview.built && <Typography data-testid="shadow-step-text" sx={{ fontSize: 13, color: A2.label, ...WRAP }}>{preview.text}</Typography>}
        {/* The eBay refund route (2026-10-29): the main panel's own line. */}
        {preview.ebayRefundRoute && <EbayRefundRouteNote route={preview.ebayRefundRoute} testId="shadow-step-ebay-refund-route" sx={{ mt: 0 }} />}
        {(preview.warnings ?? []).map((w, i) => <Typography key={i} data-testid="shadow-step-warning" sx={{ fontSize: 12, color: 'warning.main' }}>{w}</Typography>)}
        {/* One step from several of the agent's actions (2026-10-28): each
            sentence that led to it. */}
        {(preview.sources ?? []).length > 1 && (
          <Box data-testid="shadow-step-sources">
            {preview.sources.map((t, i) => <Typography key={i} sx={{ fontSize: 12, color: A2.label, ...WRAP }}>Agent: {t}</Typography>)}
          </Box>
        )}
        {/* The stage choice's labels step (2026-10-29): each label with its
            tracking link and a copy button, as the main panel's step 1. */}
        {(preview.links ?? []).length > 0 && (
          <Stack spacing={0.25} data-testid="shadow-step-links">
            {preview.links.map((l) => (
              <Stack key={l.label} direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontFamily: 'monospace', flexShrink: 0 }}>{l.label}</Typography>
                <Link href={l.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 12, fontFamily: 'monospace', color: A2.link, minWidth: 0, flex: 1, ...WRAP }}>{l.url}</Link>
                <CopyButton value={l.url} ariaLabel={`copy the tracking link for ${l.label}`} />
              </Stack>
            ))}
          </Stack>
        )}
        {/* The Return Status stage choice (2026-10-29): the main panel's own
            controls (TaskPathChoiceControls.jsx), saved as a step edit; the
            reply below follows the choice. Nothing is processed here. */}
        {fields.includes('stage') && (() => {
          const stage = shadowStageChoice(preview, values)
          return (
            <Box>
              {values.notNeeded ? (
                <Typography data-testid="shadow-step-not-needed" sx={{ fontSize: 12, color: A2.label, fontStyle: 'italic' }}>
                  {STEP_NOT_NEEDED_NOTE}
                  <Link component="button" type="button" data-testid="shadow-step-not-needed-undo" sx={{ ml: 0.75, fontSize: 12 }} onClick={() => update({ notNeeded: false })}>Undo</Link>
                </Typography>
              ) : (
                <>
                  <TaskPathChoiceControls testId="shadow-task-path-choice" testIdPrefix="shadow-task-path"
                    options={stage.options} value={stage.value} onChange={(v) => update({ stage: v })}
                    preselected={stage.preselected} date={stage.date} onDateChange={(v) => update({ date: v })} />
                  <Link component="button" type="button" data-testid="shadow-step-mark-not-needed" sx={{ fontSize: 12, textAlign: 'left' }}
                    onClick={() => update({ notNeeded: true })}>{STEP_MARK_NOT_NEEDED_LABEL}</Link>
                </>
              )}
            </Box>
          )
        })()}
        {fields.includes('reasonCode') && (
          <Box>
            <FieldLabel>Reason code</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select value={values.reasonCode ?? ''} displayEmpty data-testid="shadow-step-reason"
                SelectDisplayProps={{ 'aria-label': 'Reason code' }}
                onChange={(ev) => update({ reasonCode: ev.target.value, zeroDollarLabel: undefined })} sx={{ ...INPUT_SX['& .MuiOutlinedInput-root'], '& .MuiSelect-select': { py: '6px', px: '8px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: A2.border } }}>
                <MenuItem value=""><em>Choose a reason code</em></MenuItem>
                {reasonOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            {/* The $0 RMA (2026-10-29): the banner and the optional label
                step, unchecked by default (2026-10-30); a new reason code
                starts off again. The shadow draft never sends: the main
                panel's step carries the choice that Process uses. */}
            {zeroDollar && (
              <ZeroDollarRmaBanner code={values.reasonCode} what={preview.kind === 'replacement' ? 'replacement' : 'refund'} typical={zeroDollarTypical}
                checked={zeroDollarLabelOn(values)} onChange={(checked) => update({ zeroDollarLabel: checked ? true : undefined })} />
            )}
          </Box>
        )}
        {/* A $0 RMA replacement's own label step, optional (2026-10-30):
            unchecked, as on the main panel. The shadow draft never sends. */}
        {preview.type === 'create_return_label_for_replacement' && (
          <ReplacementLabelOptional checked={values.replacementLabel === true} onChange={(checked) => update({ replacementLabel: checked ? true : undefined })} />
        )}
        {fields.includes('items') && (
          <ItemsField allItems={preview.options?.allItems ?? []} selection={values.items ?? []} onChange={(items) => update({ items })} />
        )}
        {fields.includes('disposition') && (
          <Box>
            <FieldLabel>{DISPOSITION_LABEL}</FieldLabel>
            <FormControl size="small" fullWidth>
              {/* Return and Scrap only (2026-10-27): blank only while no check
                  has run; the check's result is selected. */}
              <Select value={values.disposition ?? ''} displayEmpty data-testid="shadow-step-disposition"
                SelectDisplayProps={{ 'aria-label': 'Disposition' }}
                renderValue={(v) => (v ? DISPOSITION_OPTIONS.find((o) => o.value === v)?.label ?? v : '')}
                onChange={(ev) => update({ disposition: ev.target.value })} sx={{ ...INPUT_SX['& .MuiOutlinedInput-root'], '& .MuiSelect-select': { py: '6px', px: '8px', minHeight: '1.2em' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: A2.border } }}>
                {DISPOSITION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            {!values.check && !values.disposition && <Typography data-testid="shadow-step-disposition-helper" sx={{ fontSize: 12, color: A2.label }}>{DISPOSITION_HELPER}</Typography>}
            {overriddenText(values.disposition, values.check?.disposition, asbReason) && (
              <Typography data-testid="shadow-step-disposition-overridden" sx={{ fontSize: 12, color: 'warning.main' }}>{overriddenText(values.disposition, values.check?.disposition, asbReason)}</Typography>
            )}
            {/* The customer's own return tracking (2026-10-28), linked. */}
            {values.disposition === 'return' && (preview.alreadyShippedBack?.tracking ?? []).map((t) => (
              <Typography key={t.number} data-testid="shadow-step-customer-tracking" sx={{ fontSize: 12 }}>
                {t.url ? <Link href={t.url} target="_blank" rel="noopener noreferrer">{customerReturnTrackingLine(t)}</Link> : customerReturnTrackingLine(t)}
              </Typography>
            ))}
            {stale && <Typography data-testid="shadow-step-disposition-stale" sx={{ fontSize: 12, color: 'warning.main' }}>{CHECK_STALE_TEXT}</Typography>}
            {isReturn && (stale || !values.check) && (
              <Button size="small" variant="outlined" disabled={!canCheck || checking} onClick={runCheck} data-testid="shadow-step-check-again" sx={{ mt: 0.5, textTransform: 'none', fontSize: 12, py: 0.25 }}>
                {checking ? 'Checking…' : (values.check ? 'Check again' : 'Check')}
              </Button>
            )}
            {checkError && <Typography data-testid="shadow-step-check-error" sx={{ fontSize: 12, color: 'error.main' }}>Could not check: {checkError}</Typography>}
          </Box>
        )}
        {isReturn && (
          <Typography data-testid="shadow-step-payer" sx={{ fontSize: 13, color: A2.text }}>
            Return shipping: {payer === 'company' ? 'company pays' : payer === 'customer' ? 'customer pays' : 'unknown for this reason code'}
          </Typography>
        )}
        {isReturn && payer === 'customer' && values.disposition !== 'scrap' && fields.includes('shippingPaymentOption') && (
          <Box>
            <FieldLabel>Return shipping: who pays?</FieldLabel>
            <ToggleButtonGroup size="small" exclusive value={values.shippingPaymentOption || 'customerCost'} data-testid="shadow-step-shipping-option"
              onChange={(ev, val) => { if (val) update({ shippingPaymentOption: val, labelDeduction: null }) }}
              sx={{ gap: 0.5, flexWrap: 'wrap', '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: A2.border, borderRadius: '4px !important', ml: '0 !important', fontSize: 12, textTransform: 'none', py: 0.25 } }}>
              {(preview.options?.shippingPaymentOptions ?? []).map((o) => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
        )}
        {isReturn && breakdown && (
          <Box data-testid="shadow-step-refund" sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '2px 8px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13 }}>Original Refund</Typography>
            <Typography data-testid="shadow-step-refund-original" sx={{ fontSize: 13, textAlign: 'right' }}>{money(breakdown.original)}</Typography>
            <Typography sx={{ fontSize: 13 }}>Return Label Deduction</Typography>
            {showDeductionInput ? (
              <TextField size="small" type="number" value={values.labelDeduction ?? breakdown.deduction ?? ''}
                inputProps={{ 'aria-label': 'Return label deduction', 'data-testid': 'shadow-step-refund-deduction-input', min: 0, step: '0.01', style: { width: 70, textAlign: 'right', fontSize: 13 } }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                onChange={(ev) => update({ labelDeduction: ev.target.value === '' ? null : Number(ev.target.value) })} sx={INPUT_SX} />
            ) : (
              <Typography data-testid="shadow-step-refund-deduction" sx={{ fontSize: 13, textAlign: 'right' }}>{money(breakdown.deduction)}</Typography>
            )}
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Final Refund</Typography>
            <Typography data-testid="shadow-step-refund-final" sx={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>{money(breakdown.final)}</Typography>
            {breakdown.deductionSource && <Typography sx={{ fontSize: 11, color: A2.label, gridColumn: '1 / -1' }}>Deduction: {DEDUCTION_SOURCE_LABELS[breakdown.deductionSource] ?? breakdown.deductionSource}</Typography>}
            {breakdown.deduction == null && <Typography data-testid="shadow-step-refund-unknown" sx={{ fontSize: 11, color: 'warning.main', gridColumn: '1 / -1' }}>The label deduction is not known: a person fills it in.</Typography>}
          </Box>
        )}
        {labelShown && (
          // The label section (2026-10-27): the main panel's own
          // ReturnLabelFields, saved with the step's edit; the calls follow it.
          <ReturnLabelFields
            value={{ ...values, ccAddress: values.ccAddress || undefined }}
            onChange={(patch) => update(patch)}
            packages={values.packages ?? [{ weight: '' }]}
            onPackagesChange={(packages) => update({ packages: packages.map((p) => ({ weight: p.weight === '' ? null : p.weight })) })}
            defaultEmail=""
            ccCompany={preview.options?.label?.company ?? null}
            weightNote={preview.options?.label?.weightNote ?? null}
            showErrors
            testId="shadow-step-label-fields"
            testIdPrefix="shadow-step-label"
          />
        )}
        {fields.includes('shippingMethod') && (
          <Box>
            <FieldLabel>Shipping method</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select value={values.shippingMethod ?? ''} displayEmpty data-testid="shadow-step-shipping"
                SelectDisplayProps={{ 'aria-label': 'Shipping method' }}
                onChange={(ev) => update({ shippingMethod: ev.target.value })} sx={{ ...INPUT_SX['& .MuiOutlinedInput-root'], '& .MuiSelect-select': { py: '6px', px: '8px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: A2.border } }}>
                {(preview.options?.shippingMethods ?? []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        )}
        {fields.includes('address') && (
          <Box>
            <FieldLabel>Ship to</FieldLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' }}>
              {ADDRESS_FIELDS.map(([field, label]) => (
                <TextField key={field} size="small" placeholder={label} value={values.address?.[field] ?? ''}
                  inputProps={{ 'aria-label': label, 'data-testid': `shadow-step-address-${field}` }}
                  onChange={(ev) => update({ address: { ...(latest.current.address ?? {}), [field]: ev.target.value } })}
                  sx={INPUT_SX} />
              ))}
            </Box>
          </Box>
        )}
        {fields.includes('decision') && (
          <Box>
            <FieldLabel>eBay cancel request</FieldLabel>
            <ToggleButtonGroup size="small" exclusive value={decision ?? values.decision ?? ''} data-testid="shadow-step-decision"
              onChange={(ev, val) => { if (val) { update({ decision: val }); onDecision?.(val) } }}
              sx={{ gap: 0.5, flexWrap: 'wrap', '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: A2.border, borderRadius: '4px !important', ml: '0 !important', fontSize: 12, textTransform: 'none', py: 0.25 } }}>
              {(preview.options?.decisions ?? []).map((d) => <ToggleButton key={d} value={d}>{d === 'approve' ? 'Approve' : 'Reject'}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
        )}
        {/* A shipped order's reject sends eBay its tracking (2026-10-28):
            its own line, so it reads as a step of what Process does. */}
        {preview.thenLine && (
          <Typography data-testid="shadow-step-then-line" sx={{ fontSize: 12.5, color: A2.text }}>{preview.thenLine}</Typography>
        )}
        {fields.includes('refundType') && (
          <Box>
            <FieldLabel>Refund</FieldLabel>
            <ToggleButtonGroup size="small" exclusive value={values.refundType ?? ''} data-testid="shadow-step-refund-type"
              onChange={(ev, val) => { if (val) update(refundTypeChange(val, preview, { full: FULL_REFUND_COMMENT, partial: PARTIAL_REFUND_COMMENT })) }}
              sx={{ gap: 0.5, flexWrap: 'wrap', '& .MuiToggleButtonGroup-grouped': { border: 1, borderColor: A2.border, borderRadius: '4px !important', ml: '0 !important', fontSize: 12, textTransform: 'none', py: 0.25 } }}>
              <ToggleButton value="full">Full refund</ToggleButton>
              <ToggleButton value="partial">Partial refund</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {fields.includes('amount') && (
          <Box>
            <FieldLabel htmlFor={id('amount')}>Amount</FieldLabel>
            <TextField id={id('amount')} size="small" type="number" value={values.amount ?? ''} disabled={values.refundType === 'full'}
              inputProps={{ 'aria-label': 'Refund amount', 'data-testid': 'shadow-step-amount', min: 0, max: preview.options?.maxAmount ?? undefined, step: '0.01', style: { width: 90, fontSize: 13 } }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText={preview.options?.maxAmount != null ? `At most the order total, ${money(preview.options.maxAmount)}` : undefined}
              onChange={(ev) => update({ amount: ev.target.value === '' ? null : Number(ev.target.value) })} sx={INPUT_SX} />
          </Box>
        )}
        {fields.includes('comment') && (
          <Box>
            <FieldLabel htmlFor={id('comment')}>Comment to the buyer</FieldLabel>
            <TextField id={id('comment')} size="small" fullWidth value={values.comment ?? ''}
              inputProps={{ 'data-testid': 'shadow-step-comment' }}
              onChange={(ev) => update({ comment: ev.target.value })} sx={INPUT_SX} />
          </Box>
        )}
        {fields.includes('messageText') && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              {/* An INR shipment proof's text is the comment to eBay (2026-10-27). */}
              <FieldLabel htmlFor={id('messageText')}>{preview.kind === 'shipment_info' ? 'Comment to eBay' : 'Message to buyer'}</FieldLabel>
              <CopyButton value={values.messageText ?? ''} ariaLabel={preview.kind === 'shipment_info' ? 'copy the comment to eBay' : 'copy the message to the buyer'} />
            </Stack>
            <TextField id={id('messageText')} multiline minRows={3} fullWidth value={values.messageText ?? ''}
              inputProps={{ 'data-testid': 'shadow-step-message' }}
              onChange={(ev) => update({ messageText: ev.target.value })} sx={TEXTAREA_SX} />
          </Box>
        )}
        {fields.includes('packages') && !isReturn && (
          <Box data-testid="shadow-step-packages">
            <FieldLabel>Packages</FieldLabel>
            <PackagesEditor packages={values.packages ?? [{ weight: '' }]} onChange={(packages) => update({ packages: packages.map((p) => ({ weight: p.weight === '' ? null : p.weight })) })} />
          </Box>
        )}
        {fields.includes('sendTo') && (
          <Box>
            <FieldLabel htmlFor={id('sendTo')}>Send label to</FieldLabel>
            <TextField id={id('sendTo')} size="small" fullWidth value={values.sendTo ?? ''} placeholder="The order's email address"
              inputProps={{ 'data-testid': 'shadow-step-send-to' }}
              onChange={(ev) => update({ sendTo: ev.target.value })} sx={INPUT_SX} />
          </Box>
        )}
        {fields.includes('note') && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FieldLabel htmlFor={id('note')}>{preview.kind === 'note' ? 'Order note' : 'Note'}</FieldLabel>
              <CopyButton value={values.note ?? ''} ariaLabel="copy the staged order note" />
            </Stack>
            <TextField id={id('note')} multiline minRows={2} fullWidth value={values.note ?? ''}
              inputProps={{ 'data-testid': 'shadow-step-note' }}
              onChange={(ev) => update({ note: ev.target.value })} sx={TEXTAREA_SX} />
          </Box>
        )}
        {preview.kind === 'support_email' && preview.options?.email && (
          // The Return Status overdue refund's email to Support (2026-10-29),
          // as the main panel's step shows it. Nothing is sent here.
          <Box data-testid="shadow-support-email" sx={{ fontSize: 13 }}>
            {['to', 'from', 'subject'].map((f) => (
              <Typography key={f} sx={{ fontSize: 13, ...WRAP }}><b>{f === 'to' ? 'To' : f === 'from' ? 'From' : 'Subject'}:</b> {preview.options.email[f] ?? ''}</Typography>
            ))}
            <Typography data-testid="shadow-support-email-body" sx={{ fontSize: 13, whiteSpace: 'pre-wrap', mt: 0.5, ...WRAP }}>{preview.options.email.body ?? ''}</Typography>
          </Box>
        )}
        {preview.readOnly && values.note && (
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Typography data-testid="shadow-draft-note" sx={{ flex: 1, minWidth: 0, fontSize: 13, whiteSpace: 'pre-wrap', ...WRAP }}>{values.note}</Typography>
            <CopyButton value={values.note} ariaLabel="copy the staged order note" />
          </Stack>
        )}
        {preview.part?.link && (
          <Link href={preview.part.link} target="_blank" rel="noopener noreferrer" data-testid="shadow-step-part-link" sx={{ fontSize: 12, color: A2.link, ...WRAP }}>{preview.part.link}</Link>
        )}
        {((!viewCall && !callList.length && callNote) || ((editable || savable) && status !== 'idle')) && (
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {!viewCall && !callList.length && callNote && <Typography data-testid="shadow-step-call-note" sx={{ fontSize: 12, color: A2.label }}>{callNote}</Typography>}
            {(editable || savable) && status !== 'idle' && (
              <Typography data-testid="shadow-step-save-status" sx={{ fontSize: 12, color: status === 'error' ? 'error.main' : A2.label }}>
                {status === 'saving' || status === 'pending' ? 'Saving…' : status === 'saved' ? 'Saved' : `Could not save: ${error}`}
              </Typography>
            )}
            {(editable || savable) && status === 'saved' && editedAt && (
              <Typography data-testid="shadow-step-edited-at" sx={{ fontSize: 12, color: A2.label }}>{editedAtLabel(editedAt)}</Typography>
            )}
          </Stack>
        )}
    </StepRow>
  )
}

// The reply as it would go: From / To / CC / Subject for an email, read
// only, and the body, read only, its placeholders highlighted, with the copy
// control; then one line naming the placeholders left (2026-10-23, the A2
// panel: one warning only).
// replyDestination (2026-10-27, @cxhub/shared's reply-destination.js): where
// the reply would go, the line under the To line ("Sends to eBay inquiry
// <id>"); with no reply on a case waiting for the buyer, "No reply: the
// inquiry is waiting for the buyer" in place of "No reply staged."
// waitingLine (2026-10-29): the reply waits for a choice (the Return Status
// stage): this line in place of "No reply staged.".
function ReplySection({ draft, reply, replyHeaders, signature, english, placeholderText, fetchInvoices, replyDestination = null, waitingLine = null }) {
  const headers = replyHeaderRows(replyHeaders)
  const destLine = replyDestination ? replyDestinationLine(replyDestination, { hasReply: Boolean(reply) }) : null
  return (
    <A2ReplySection testId="shadow-draft-reply-section">
      {/* No reply staged (2026-10-20): the agent chose none, e.g. an eBay
          case waiting for the buyer; What's next says why. */}
      {!reply && waitingLine && <Typography data-testid="shadow-draft-reply-waiting" sx={{ fontSize: 13, color: A2.label }}>{waitingLine}</Typography>}
      {!reply && !waitingLine && !(destLine && replyDestination?.noReply) && <Typography data-testid="shadow-draft-no-reply" sx={{ fontSize: 13, color: A2.label }}>No reply staged.</Typography>}
      {destLine && !headers.length && <Typography data-testid="shadow-draft-reply-destination" sx={{ fontSize: 12, color: A2.label }}>{destLine}</Typography>}
      {reply && headers.length > 0 && (
        <Box data-testid="shadow-draft-reply-headers" sx={{ display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr)', gap: '6px 8px', alignItems: 'center' }}>
          {headers.map(([label, value]) => [
            <Typography key={`${label}-l`} sx={{ fontSize: 12, color: A2.label }}>{label}</Typography>,
            <Box key={`${label}-v`} data-testid={`shadow-draft-reply-${label.toLowerCase()}`} sx={{ fontSize: 13, color: A2.text, border: 1, borderColor: A2.border, borderRadius: '6px', px: '8px', py: '6px', minHeight: 31, boxSizing: 'border-box', ...WRAP }}>{value}</Box>,
          ])}
        </Box>
      )}
      {destLine && headers.length > 0 && <Typography data-testid="shadow-draft-reply-destination" sx={{ fontSize: 12, color: A2.label }}>{destLine}</Typography>}
      {reply && (
        <Box sx={{ border: 1, borderColor: A2.border, borderRadius: '6px', px: '14px', py: '12px' }}>
          {/* The copy-to-main-panel control: copies the reply as written. */}
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Typography component="pre" data-testid="shadow-draft-reply" sx={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', ...WRAP, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.45, color: A2.text, m: 0 }}>
              <PlaceholderHighlight text={reply} />
            </Typography>
            <CopyButton value={reply} ariaLabel="copy the agent's reply" />
          </Stack>
          {signature && (
            <Box data-testid="shadow-draft-signature" sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 11, color: A2.label }}>Signature (added by the reply box)</Typography>
              <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', ...WRAP, fontFamily: 'inherit', fontSize: 12, color: A2.label, m: 0 }}>{signature}</Typography>
            </Box>
          )}
        </Box>
      )}
      <EnglishBox text={english?.reply} testId="shadow-draft-reply-english" />
      {/* The reply says an invoice is attached but none is (2026-10-19). */}
      {draft.invoiceClaimMismatch && <Alert severity="warning" data-testid="shadow-draft-invoice-mismatch">The reply says an invoice is attached, but none is.</Alert>}
      {!draft.pipelineNotUsed && <InvoicesBox draft={draft} fetchInvoices={fetchInvoices} />}
      {/* The pipeline draft's own review reasons (2026-10-21): they apply to
          this draft only, not to the suggestion shown. */}
      {draft.pipelineNotUsed && draft.reviewReason && (
        <Alert severity="info" data-testid="shadow-draft-pipeline-review-reason" sx={WRAP}>Review reasons for this draft: {draft.reviewReason}</Alert>
      )}
      {/* The placeholders left (2026-10-19; one line since 2026-10-23): the
          person checks the carrier and fills them in. Copy still copies the
          reply as is. Last, so the section ends right after it. */}
      <PlaceholderLine text={placeholderText} testId="shadow-draft-placeholder-warning" />
    </A2ReplySection>
  )
}

// showTranslation / translation (2026-10-19): the header's Translate button
// and its shadowDraft translation. The Copy buttons keep copying the original.
// signature (2026-10-19): the signature the reply box adds for this contact,
// shown under the reply and never copied with it.
// translationStatus (2026-10-20): the agent draft's own Translate fetch
// (useShadowTranslation.js): 'loading' shows "Translating…", 'error'
// "Translation unavailable"; the rest of the column is unaffected.
// replyHeaders (2026-10-23): { from, to, cc, subject } of the reply box for
// an email reply, shown read only; null for an eBay message.
// The A2 layout (2026-10-23, AgentDraftLayout.jsx, shared with the main
// panel): Summary (blue), What's next (condensed), Steps, Reply, then the
// run's Details collapsed and the "Preview only" line.
// replyDestination (2026-10-27): the suggestion's (modelMeta.replyDestination),
// for the reply's destination line.
// trackingLinks (2026-10-29): the suggestion's modelMeta.nextStepTrackingLinks,
// for What's next's tracking links (NextStepTracking.jsx).
export function ShadowDraftBody({ draft, showTranslation = false, translation = null, translationStatus = null, signature = null, replyHeaders = null, fetchInvoices = realFetchInvoiceAttachments, saveStepEdit = realSaveShadowStepEdit, saveResolution = realSaveShadowResolution, replyDestination = null, trackingLinks = null }) {
  const english = shadowTranslationFor(draft, showTranslation, translation)
  const returnLabels = useReturnLabels(draft?.conversationId ?? null)
  // The replacement rows' live values (2026-10-29): its label row hides while
  // the $0 RMA box is checked.
  const [replValues, setReplValues] = useState({})
  const orderBanners = orderWaitingBanners(returnLabels.data)
  const lines = toolCallLines(draft.toolCalls)
  const allSteps = draft.status === 'ok' ? shadowStepRows(draft) : []
  // Replacement or refund on the step (2026-10-27): the path in effect per
  // step group, switched here at once (both paths were built) and saved on
  // the draft (stepEdits). Only the path in effect shows; its reply shows.
  const [resPaths, setResPaths] = useState({})
  const [resError, setResError] = useState(null)
  // An eBay cancel request (2026-10-28): the decision per request, as
  // chosen here; only the decision's pair of steps shows.
  const [decisions, setDecisions] = useState({})
  const res = shadowResolutionState(allSteps, resPaths, decisions)
  // The Return Status stage choice (2026-10-29): the reply follows the
  // choice (the stage's reply with its date), and waits for one.
  const stageStep = allSteps.find((p) => p?.kind === 'task_path_choice' && p.options?.stageChoice)
  const [stageValues, setStageValues] = useState(() => (stageStep ? { ...(stageStep.values ?? {}), notNeeded: stageStep.notNeeded === true } : null))
  const stageReply = stageStep ? shadowStageReply(stageStep, stageValues) : null
  // "Found the right part?" (2026-10-30, correctPart.js): the part the
  // admin found switches this column's reply (a preview: copy it into the
  // main panel to send), and clears the "Double-check" line.
  const [partResult, setPartResult] = useState(null)
  const reply = partResult?.reply ?? (stageReply ? stageReply.text : (res.reply ?? shadowReplyText(draft)))
  const showCorrectPart = !partResult && draft.status === 'ok' && Boolean(draft.suggestionId) && correctPartBoxApplies({ intent: draft.intent ?? null, nextSteps: draft.nextSteps ?? [] })
  const placeholderText = placeholderLine(reply)
  const sourcesLine = shadowSourcesLine(draft)
  // With the agent's draft as the main one (2026-10-21), this column shows
  // the pipeline's; the sources are still the agent's, so they say so.
  const sources = sourcesLine && draft.pipelineNotUsed ? `Agent: ${sourcesLine}` : sourcesLine
  const limitWarning = shadowLimitWarning(draft)
  const steps = allSteps
  const chooseResolution = (group) => (path, why) => {
    setResPaths((prev) => ({ ...prev, [group]: path }))
    setResError(null)
    if (draft?.id && draft?.conversationId) saveResolution(draft.conversationId, draft.id, group, { path, why }).catch((err) => setResError(err?.message ?? 'unknown error'))
  }
  const holdPath = draft.status === 'ok' ? shadowSalesHoldPath(draft) : null
  const confidence = confidenceView(partResult ? confidenceAfterCorrectPart(draft.confidence) : draft.confidence, { agentPrefix: Boolean(draft.pipelineNotUsed) })
  // The step checkboxes (2026-10-26): every step checked unless its saved
  // edit says otherwise; steps that run together are unchecked together
  // (processAll.js). Saved with each step's edit; nothing is processed here.
  const stepTypes = steps.map((p) => ({ type: p.type ?? null, key: p.key, runsWithKey: p.runsWithKey ?? null }))
  const [unchecked, setUnchecked] = useState(() => steps.map((p, i) => (p.checked === false ? i : null)).filter((i) => i !== null))
  const onCheck = (i) => (value) => setUnchecked((prev) => toggleStepCheck({ actions: stepTypes, unchecked: prev, index: i, checked: value }).unchecked)
  // The English of a What's next item, by the step's own text (an item
  // merged from several steps has none).
  const nextEnglish = (text) => {
    const i = (draft.nextSteps ?? []).findIndex((s) => String(s ?? '').trim() === text)
    const en = i >= 0 ? english?.nextSteps?.[i] : null
    return en && en !== text ? <EnglishBox text={en} testId={`shadow-draft-step-english-${i}`} /> : null
  }
  return (
    // Every text block wraps, a long URL included (2026-10-19, prod contact
    // 7851347): the column never scrolls sideways.
    <AgentDraftShell testId="shadow-draft-panel">
      {/* This order's return label waiting on the OMS API (2026-10-29). */}
      {orderBanners.length > 0 && <Box sx={{ px: '20px' }}><ReturnLabelBanners banners={orderBanners} /></Box>}
      {showTranslation && (translationStatus === 'loading' || translationStatus === 'error') && (
        <Typography data-testid="shadow-translation-status" sx={{ fontSize: 12, px: '20px', color: translationStatus === 'error' ? 'warning.main' : A2.label }}>
          {translationStatus === 'loading' ? 'Translating…' : 'Translation unavailable'}
        </Typography>
      )}
      {/* What the customer is asking for or needs now, from the whole
          conversation and the order (2026-10-16). Older drafts have none. */}
      {(draft.summary || confidence) && (
        <SummaryBlock summary={draft.summary} testId="shadow-draft-summary" right={<ConfidenceLabel confidence={confidence} part="chip" />}>
          <ConfidenceLabel confidence={confidence} part="reasons" />
          <EnglishBox text={english?.summary} testId="shadow-draft-summary-english" />
        </SummaryBlock>
      )}
      <NextStepsSection steps={partResult ? stepsAfterCorrectPart(draft.nextSteps ?? [], partResult.steps ?? []) : (draft.nextSteps ?? [])} testId="shadow-draft-next-steps" renderEnglish={nextEnglish} trackingLinks={trackingLinks}
        top={limitWarning ? <Alert severity="info" data-testid="shadow-draft-limit">{limitWarning}</Alert> : null} />
      {showCorrectPart && <CorrectPartBox suggestionId={draft.suggestionId} onUse={(r) => setPartResult(r)} testId="shadow-correct-part-box" />}
      {draft.status === 'ok' ? (
        <>
          {/* Steps (2026-10-23): the agent's actions as the pipeline's steps,
              editable; the staged order note inside the step that takes a
              note, or its own step when none does. */}
          {/* The sales-hold path choice (2026-10-27): the same choice as the
              main panel, preselected with the agent's own conclusion. A
              preview: switching happens on the main panel. */}
          {holdPath && (
            <Box sx={{ bgcolor: A2.section, px: '20px', py: '8px' }} data-testid="shadow-sales-hold-path">
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: A2.label }}>Path (the agent's preselection)</Typography>
              <RadioGroup row value={holdPath.path}>
                {holdPath.options.map((o) => (
                  <FormControlLabel key={o.value} value={o.value} disabled control={<Radio size="small" inputProps={{ 'data-testid': `shadow-sales-hold-path-${o.value}` }} />}
                    label={<Typography sx={{ fontSize: 13 }}>{o.label}</Typography>} />
                ))}
              </RadioGroup>
            </Box>
          )}
          {/* The pipeline's steps not used (2026-10-30): with the agent's own
              steps on the main panel, this column lists the pipeline's,
              never processed. */}
          {draft.stepsShownAgent && (draft.pipelineSteps ?? []).length > 0 && (
            <Box sx={{ bgcolor: A2.section, px: '20px', py: '8px' }} data-testid="shadow-pipeline-steps-not-used">
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: A2.label }}>{PIPELINE_STEPS_NOT_USED_TITLE}</Typography>
              {draft.pipelineSteps.map((s, i) => (
                <Typography key={i} sx={{ fontSize: 13, ...WRAP }} data-testid="shadow-pipeline-step-not-used">{i + 1}. {s.label}{s.system ? ` (${s.system})` : ''}</Typography>
              ))}
            </Box>
          )}
          {/* The pipeline's order note not used (2026-11-02): read only,
              never processed. */}
          {pipelineNoteNotUsed(draft) && (
            <Box sx={{ bgcolor: A2.section, px: '20px', py: '8px' }} data-testid="shadow-pipeline-note-not-used">
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: A2.label }}>{PIPELINE_NOTE_NOT_USED_TITLE}</Typography>
              <Typography data-testid="shadow-pipeline-note-not-used-text" sx={{ fontSize: 13, whiteSpace: 'pre-wrap', ...WRAP }}>{pipelineNoteNotUsed(draft)}</Typography>
            </Box>
          )}
          {steps.length > 0 && !draft.stepsShownAgent && (
            <StepsSection count={res.shown.length} testId="shadow-draft-steps">
              {res.shown.filter(({ preview: p }) => !shadowReplacementLabelHeld(p, steps, replValues) && shadowStageStepShown(p, stageStep, stageValues)).map(({ preview: p, index: i, number, bar }) => (
                <Box key={`${draft.id ?? draft.suggestionId}-${p.key}`}>
                  {bar && <ResolutionBar view={bar} onChoose={chooseResolution(bar.group)} testId="shadow-resolution-bar" />}
                  <ShadowStepRow index={number - 1} preview={p} draft={draft} saveEdit={saveStepEdit}
                    checked={!unchecked.includes(i)} onCheck={onCheck(i)} checkNote={stepDependencyNote(stepTypes, i)}
                    decision={p.decisionGroup ? shadowDecisionOf(steps, p.decisionGroup, decisions) : null}
                    onDecision={p.decisionGroup ? (d) => setDecisions((prev) => ({ ...prev, [p.decisionGroup]: d })) : null}
                    onValues={p === stageStep ? setStageValues : (isShadowReplacementRow(p) ? (v) => setReplValues((prev) => ({ ...prev, [p.key]: v })) : null)} />
                </Box>
              ))}
              {resError && <Typography data-testid="shadow-resolution-error" sx={{ fontSize: 12, color: 'error.main' }}>Could not save the choice: {resError}</Typography>}
              <EnglishBox text={english?.orderNote} testId="shadow-draft-note-english" />
            </StepsSection>
          )}
          <ReplySection draft={draft} reply={reply} replyHeaders={replyHeaders} signature={signature} english={partResult ? { ...(english ?? {}), reply: partResult.english ?? null } : (stageReply ? { ...(english ?? {}), reply: null } : english)} placeholderText={placeholderText} fetchInvoices={fetchInvoices} replyDestination={draft.pipelineNotUsed ? null : replyDestination} waitingLine={stageReply && !stageReply.text ? STAGE_REPLY_PLACEHOLDER : null} />
          {/* The main panel's one button, as a preview (2026-10-27): the
              same label the CSR would get, disabled. Never posts. */}
          {!draft.pipelineNotUsed && (
            <PrimaryActionArea testId="shadow-draft-primary-action" onClick={() => {}}
              state={{ label: processAllButtonLabel(shadowProcessableCount(steps, [...unchecked, ...res.hiddenIndexes]), { reply: Boolean(reply) }), line: SHADOW_BUTTON_PREVIEW_LINE, disabled: true, warn: false }} />
          )}
        </>
      ) : (
        <Box sx={{ bgcolor: A2.section, px: '20px', py: '12px' }}>
          <Typography color="error" sx={{ fontSize: 13 }}>{draft.error ?? 'The agent draft failed.'}</Typography>
        </Box>
      )}
      <DetailsFooter
        testId="shadow-draft-details"
        runLine={draft.pipelineNotUsed ? null : runDetailsLine(draft)}
        sources={sources}
        toolLines={lines}
        footer={<Typography data-testid="shadow-draft-preview-only" sx={{ fontSize: 12, fontWeight: 600, color: A2.label, pt: '6px', borderTop: 1, borderColor: A2.rule, mt: '4px' }}>{PREVIEW_ONLY_TEXT}</Typography>}
      />
    </AgentDraftShell>
  )
}

// How sure the agent is (2026-10-23): reasons on hover; on Low always
// visible. Shared with the main panel's agent draft. Since 2026-10-26 the
// chip sits on the Summary label row, right-aligned (part 'chip'), and the
// always-shown reasons are a line under the summary (part 'reasons').
export function ConfidenceLabel({ confidence, testId = 'shadow-draft-confidence', part = 'chip' }) {
  if (!confidence) return null
  if (part === 'reasons') {
    if (!(confidence.alwaysShowReasons && confidence.reasons)) return null
    return <Typography data-testid={`${testId}-reasons`} sx={{ fontSize: 12, color: A2.label, mt: '2px', ...WRAP }}>{confidence.reasons}</Typography>
  }
  const color = confidence.level === 'low' ? 'error.main' : confidence.level === 'medium' ? 'warning.main' : 'success.main'
  return (
    <Box component="span" data-testid={testId} data-level={confidence.level} sx={{ display: 'inline-flex' }}>
      <Tooltip title={confidence.reasons || ''} placement="bottom-end">
        <Box component="span" data-testid={`${testId}-label`} sx={{ display: 'inline-block', fontSize: 11, fontWeight: 700, px: '6px', py: '1px', borderRadius: '4px', border: 1, borderColor: color, color, bgcolor: A2.section, whiteSpace: 'nowrap' }}>
          {confidence.label}
        </Box>
      </Tooltip>
    </Box>
  )
}

// The right-hand column, side by side with the current suggestion.
export function ShadowCompareColumn({ draft, onCollapse, showTranslation = false, translation = null, translationStatus = null, signature = null, replyHeaders = null, replyDestination = null, trackingLinks = null }) {
  return (
    <Box data-testid="shadow-compare" sx={{ borderLeft: 1, borderColor: 'divider', bgcolor: A2.panel, minHeight: '100%', pb: 2 }}>
      {/* When this draft was written (2026-10-26), above the header. */}
      <GeneratedAtLine at={draft.createdAt} testId="shadow-draft-generated-at" sx={{ bgcolor: A2.section, px: '20px', pt: '8px' }} />
      <AgentDraftHeader title={comparisonTitle(draft)} titleTestId="shadow-compare-title" chips={<StatusChips draft={draft} />} sx={{ mb: '8px' }}
        action={(
          <Button size="small" data-testid="shadow-compare-toggle" onClick={onCollapse} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
            {draft.pipelineNotUsed ? 'Hide comparison ›' : 'Hide agent draft ›'}
          </Button>
        )} />
      <ShadowDraftBody key={draft.id ?? draft.suggestionId ?? undefined} draft={draft} showTranslation={showTranslation} translation={translation} translationStatus={translationStatus} signature={signature} replyHeaders={replyHeaders} replyDestination={replyDestination} trackingLinks={trackingLinks} />
    </Box>
  )
}

// Collapsed beside the current suggestion: a thin rail that says a draft
// exists, and reopens the column.
export function ShadowCompareRail({ onExpand, title = 'Agent draft' }) {
  return (
    <Box data-testid="shadow-compare" sx={{ borderLeft: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 2, width: 32, flexShrink: 0 }}>
      <Button
        data-testid="shadow-compare-toggle"
        aria-label={`Show ${title.toLowerCase()}`}
        title={`Show ${title.toLowerCase()}`}
        onClick={onExpand}
        sx={{ minWidth: 0, p: 0.5, writingMode: 'vertical-rl', textTransform: 'none', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
      >
        ‹ {title} ✓
      </Button>
    </Box>
  )
}

// A narrow viewport: under the current suggestion, its header always shown so
// it can be reopened.
export function ShadowCompareStacked({ draft, collapsed, onToggle, showTranslation = false, translation = null, translationStatus = null, signature = null, replyHeaders = null, replyDestination = null, trackingLinks = null }) {
  return (
    <Box data-testid="shadow-compare" sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderStyle: 'dashed', borderRadius: 1, bgcolor: A2.panel, overflow: 'hidden' }}>
      {!collapsed && <GeneratedAtLine at={draft.createdAt} testId="shadow-draft-generated-at" sx={{ bgcolor: A2.section, px: '20px', pt: '8px' }} />}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} sx={{ bgcolor: A2.section, px: '20px', py: '10px', mb: collapsed ? 0 : '8px' }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography fontWeight={700} fontSize="0.875rem" data-testid="shadow-compare-title">{comparisonTitle(draft)}{collapsed ? ' ✓' : ''}</Typography>
          <StatusChips draft={draft} />
        </Stack>
        <Button size="small" data-testid="shadow-compare-toggle" onClick={onToggle} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
          {/* The pipeline draft opens on demand (2026-11-02): only its close. */}
          {draft.pipelineNotUsed && !collapsed ? 'Hide comparison ▴' : `${collapsed ? 'Show' : 'Hide'} ${comparisonTitle(draft).replace(/ \(not used\)$/, '').toLowerCase()} ${collapsed ? '▾' : '▴'}`}
        </Button>
      </Stack>
      {!collapsed && <ShadowDraftBody key={draft.id ?? draft.suggestionId ?? undefined} draft={draft} showTranslation={showTranslation} translation={translation} translationStatus={translationStatus} signature={signature} replyHeaders={replyHeaders} replyDestination={replyDestination} trackingLinks={trackingLinks} />}
    </Box>
  )
}
